import { useCallback, useEffect, useRef, useState } from 'react';
import { normalize } from '@/lib/tax/tax-utils';
import { apiGet, operationsClient } from '@/lib/api';
import {
  isValidPostalCode,
  normalizePostalAreaCode,
  postalAreaLookupCodes,
} from '@/lib/postal-code';
import type { TaxGroup, TaxResolutionResult, TaxResolvedFrom, TaxZone } from '@/types/tax';

interface UseEstimateTaxResolutionArgs {
  city: string;
  state: string;
  zipDebounceMs?: number;
}

function unpackTaxGroups(res: TaxGroup[] | { data: TaxGroup[] }): TaxGroup[] {
  return Array.isArray(res) ? res : (res.data ?? []);
}

export function useEstimateTaxResolution({
  city,
  state,
  zipDebounceMs = 700,
}: UseEstimateTaxResolutionArgs) {
  const [taxResolution, setTaxResolution] = useState<TaxResolutionResult | null>(null);
  const [taxLoading, setTaxLoading] = useState(false);
  const zipTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const taxRequestTokenRef = useRef(0);
  const taxGroupsRef = useRef<TaxGroup[]>([]);
  const taxGroupsLoadedRef = useRef(false);
  const taxGroupsPromiseRef = useRef<Promise<TaxGroup[]> | null>(null);

  const loadTaxGroups = useCallback(async (): Promise<TaxGroup[]> => {
    if (taxGroupsLoadedRef.current) return taxGroupsRef.current;
    if (taxGroupsPromiseRef.current) return taxGroupsPromiseRef.current;

    const promise = apiGet<TaxGroup[] | { data: TaxGroup[] }>(operationsClient, '/tax-groups')
      .then((res) => {
        const groups = unpackTaxGroups(res);
        taxGroupsRef.current = groups;
        taxGroupsLoadedRef.current = true;
        return groups;
      })
      .finally(() => {
        taxGroupsPromiseRef.current = null;
      });

    taxGroupsPromiseRef.current = promise;
    return promise;
  }, []);

  const clearPendingTaxResolution = useCallback(() => {
    taxRequestTokenRef.current++;
    setTaxLoading(false);
    if (zipTimerRef.current) {
      clearTimeout(zipTimerRef.current);
      zipTimerRef.current = undefined;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void loadTaxGroups().catch(() => {
      if (cancelled) return;
      // Tax groups not available — non-critical
    });
    return () => {
      cancelled = true;
    };
  }, [loadTaxGroups]);

  useEffect(() => () => clearPendingTaxResolution(), [clearPendingTaxResolution]);

  const resolveTax = useCallback(
    async (zip: string, nextCity: string, nextState: string) => {
      if (!zip && !nextCity) {
        clearPendingTaxResolution();
        setTaxResolution(null);
        return;
      }

      const token = ++taxRequestTokenRef.current;
      setTaxLoading(true);

      async function getGroups(): Promise<TaxGroup[]> {
        try {
          return await loadTaxGroups();
        } catch {
          return [];
        }
      }

      async function applyDefault(groupsOverride?: TaxGroup[]) {
        const groups = groupsOverride ?? (await getGroups());
        if (token !== taxRequestTokenRef.current) return;

        const fallback = groups.find((group) => group.isDefault && group.isActive);
        if (fallback) {
          setTaxResolution({ exempt: false, group: fallback, resolvedFrom: 'ORG_DEFAULT' });
        } else {
          setTaxResolution(null);
        }
      }

      try {
        const res = await apiGet<TaxZone[] | { data: TaxZone[] }>(operationsClient, '/tax-zones');
        if (token !== taxRequestTokenRef.current) return;
        const zones: TaxZone[] = Array.isArray(res) ? res : (res.data ?? []);
        const zipLookupCodes = postalAreaLookupCodes(zip);

        const exactZipMatch = zones.find((zone) =>
          zone.zipCode ? zipLookupCodes.includes(normalizePostalAreaCode(zone.zipCode)) : false,
        );
        const cityMatch =
          !exactZipMatch && nextCity && nextState
            ? zones.find(
                (zone) =>
                  normalize(zone.city ?? '') === normalize(nextCity) &&
                  normalize(zone.stateCode ?? '') === normalize(nextState),
              )
            : null;

        const matchingZone = exactZipMatch ?? cityMatch;
        const resolvedFrom: TaxResolvedFrom = exactZipMatch
          ? 'ZIP_MATCH'
          : cityMatch
            ? 'CITY_MATCH'
            : 'NONE';

        if (matchingZone?.taxGroup) {
          setTaxResolution({ exempt: false, group: matchingZone.taxGroup, resolvedFrom });
        } else if (matchingZone?.taxGroupId) {
          const groups = await getGroups();
          if (token !== taxRequestTokenRef.current) return;

          const group = groups.find((item) => item.id === matchingZone.taxGroupId);
          if (group) {
            setTaxResolution({ exempt: false, group, resolvedFrom });
          } else {
            await applyDefault(groups);
          }
        } else {
          await applyDefault();
        }
      } catch {
        if (token !== taxRequestTokenRef.current) return;
        await applyDefault();
      } finally {
        if (token === taxRequestTokenRef.current) setTaxLoading(false);
      }
    },
    [clearPendingTaxResolution, loadTaxGroups],
  );

  const handleZipChange = useCallback(
    (zip: string, onChange: (zip: string) => void) => {
      onChange(zip);
      if (zipTimerRef.current) clearTimeout(zipTimerRef.current);
      // Fire tax resolution for either a complete US ZIP or a complete
      // Canadian postal code. The downstream resolver looks up by
      // zone.zipCode, which works for both — CA zones just need to be
      // populated separately (CanadianTaxService).
      if (isValidPostalCode(zip)) {
        zipTimerRef.current = setTimeout(() => {
          void resolveTax(zip, city, state);
        }, zipDebounceMs);
      }
    },
    [city, resolveTax, state, zipDebounceMs],
  );

  return {
    taxResolution,
    taxLoading,
    resolveTax,
    handleZipChange,
    clearPendingTaxResolution,
    setTaxResolution,
  };
}
